import React, { useEffect, useMemo, useState } from "react";
import {
    Modal,
    Button,
    Card,
    Spinner,
    Alert,
    Badge,
    Tabs,
    Tab,
} from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import { useReportPreviewTemplates } from "../hooks/useReportPreviewTemplates";
import { useErrorModalStore } from "@infrastructure/api/store/errorModalStore";
import { useScope } from "@core/hooks/useRouteParamId";
import { REPORT_SPA_QUERY_PARAMS } from "@core/reports/constants/reportSpaQueryParams";
import { subtleBadgeProps } from "@core/utils/subtleBadge";
import ActivityFormAccordion from "@features/report/activity-entry/components/ActivityFormAccordion";
import {
    getActivityAccordionKey,
    getGroupActivityContexts,
    sortSubGroups,
} from "@features/report/activity-entry/utils/activityEntryGroupHelpers";
import { mapTemplatePreviewToFormGroups } from "@features/report/template/utils/mapTemplatePreviewToFormGroups";
import type { OriginScope } from "@features/report/template/types/reportTemplate";

const ORIGIN_SCOPE_LABELS: Record<string, string> = {
    CHURCH: "Church",
    DISTRICT: "District",
    NATION: "Nation",
};

type PreviewReportTemplateModalProps = {
    show: boolean;
    onHide: () => void;
    departmentId?: number;
    reportTemplateId?: number;
    nationId?: number;
    districtId?: number;
    churchId?: number;
    title?: string;
};

const PreviewReportTemplateModal: React.FC<PreviewReportTemplateModalProps> = ({
    show,
    onHide,
    departmentId: departmentIdProp,
    reportTemplateId: reportTemplateIdProp,
    nationId: nationIdProp,
    districtId: districtIdProp,
    churchId: churchIdProp,
    title = "Report Template Preview",
}) => {
    const [searchParams] = useSearchParams();
    const routeScope = useScope();

    const reportTemplateId =
        reportTemplateIdProp ?? Number(searchParams.get(REPORT_SPA_QUERY_PARAMS.TEMPLATE_ID));
    const departmentId = departmentIdProp ?? Number(searchParams.get(REPORT_SPA_QUERY_PARAMS.DEPARTMENT_ID));

    const nationId =
        nationIdProp ??
        (routeScope?.scope === "nation" ? routeScope.scopeId : undefined) ??
        (!routeScope ? Number(searchParams.get("sid")) || undefined : undefined);
    const districtId =
        districtIdProp ?? (routeScope?.scope === "district" ? routeScope.scopeId : undefined);
    const churchId =
        churchIdProp ?? (routeScope?.scope === "church" ? routeScope.scopeId : undefined);

    const { data, loading, reload } = useReportPreviewTemplates({
        reportTemplateId,
        departmentId,
        ...(nationId ? { nationId } : {}),
        ...(districtId ? { districtId } : {}),
        ...(churchId ? { churchId } : {}),
    });

    const [openKeys, setOpenKeys] = useState<string[]>([]);
    const [activeSubGroupTab, setActiveSubGroupTab] = useState<string>("");

    const toggleKey = (key: string) => {
        setOpenKeys((prev) =>
            prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
        );
    };

    const formGroup = useMemo(() => {
        const groups = mapTemplatePreviewToFormGroups(data);
        return groups[0] ?? null;
    }, [data]);

    const tmpl = formGroup?.report_template;
    const subGroups = useMemo(
        () => sortSubGroups(formGroup?.sub_groups),
        [formGroup]
    );
    const hasSubGroups =
        Boolean(formGroup?.has_subgroups) || subGroups.length > 0;

    const activeTabKey = useMemo(() => {
        if (!hasSubGroups || subGroups.length === 0) return "";
        if (
            activeSubGroupTab &&
            subGroups.some(
                (sg) => String(sg.report_sub_group_id ?? "null") === activeSubGroupTab
            )
        ) {
            return activeSubGroupTab;
        }
        return String(subGroups[0].report_sub_group_id ?? "null");
    }, [hasSubGroups, subGroups, activeSubGroupTab]);

    const contextsForTab = useMemo(() => {
        if (!formGroup) return [];
        if (!hasSubGroups) {
            return getGroupActivityContexts(formGroup, false);
        }
        const sg = subGroups.find(
            (s) => String(s.report_sub_group_id ?? "null") === activeTabKey
        );
        if (!sg) return [];
        return getGroupActivityContexts(
            {
                ...formGroup,
                has_subgroups: true,
                sub_groups: [sg],
                activities: [],
            },
            false
        );
    }, [formGroup, hasSubGroups, subGroups, activeTabKey]);

    useEffect(() => {
        setOpenKeys(
            contextsForTab.map((c) =>
                getActivityAccordionKey(c.subGroupId, c.activity.activity_id)
            )
        );
    }, [contextsForTab]);

    const validParams =
        Number.isFinite(reportTemplateId) && Number.isFinite(departmentId);
    const showValidation = useErrorModalStore((s) => s.showValidation);

    const handleRefresh = () => {
        if (!validParams) {
            showValidation([
                "Please select a report template and department (query params rtid and did).",
            ]);
            return;
        }
        void reload();
    };

    const originScope = tmpl?.origin_scope as OriginScope | null | undefined;
    const originLabel = originScope
        ? ORIGIN_SCOPE_LABELS[originScope] || originScope
        : null;

    const renderAccordion = () => (
        <ActivityFormAccordion
            contexts={contextsForTab}
            openKeys={openKeys}
            onToggleKey={toggleKey}
            showInactive={false}
            values={{}}
            activityEditable={false}
            showSaveButton={false}
            busy={false}
            tagIsClosed={false}
            isActivityDirty={() => false}
            areAllKpisSubmittedForActivity={() => false}
            isKpiSubmitted={() => false}
            onChange={() => undefined}
            onSaveActivity={() => undefined}
            previewMode
        />
    );

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            size="xl"
            backdrop="static"
            keyboard
            scrollable
            contentClassName="border-0 shadow"
        >
            <Modal.Header closeButton className="border-0 pb-0">
                <div className="w-100">
                    <Modal.Title className="fw-bold">{title}</Modal.Title>
                    <div className="text-muted small mt-1">
                        Preview the activities and KPIs before using this template.
                    </div>
                </div>
            </Modal.Header>

            <Modal.Body className="pt-3">
                {!validParams ? (
                    <Alert variant="danger" className="mb-0">
                        Missing required query params. Expected <strong>?rtid=</strong> and{" "}
                        <strong>?did=</strong>.
                    </Alert>
                ) : loading ? (
                    <div className="py-5 text-center">
                        <Spinner animation="border" />
                        <div className="text-muted mt-2">Loading preview…</div>
                    </div>
                ) : !formGroup ? (
                    <Alert variant="light" className="border mb-0">
                        No preview data returned.
                    </Alert>
                ) : (
                    <Card className="border-0 shadow-sm">
                        <Card.Body className="p-3 p-md-4">
                            <div className="mb-3">
                                <div className="fw-bold fs-5 text-break">
                                    {tmpl?.report_template_name ?? "Report Template Preview"}
                                </div>
                                <div className="d-flex flex-wrap gap-2 mt-2">
                                    <Badge
                                        {...subtleBadgeProps("secondary", "border")}
                                        pill
                                    >
                                        Department:{" "}
                                        <span className="fw-semibold">
                                            {tmpl?.department_name ?? "—"}
                                        </span>
                                    </Badge>
                                    {originLabel ? (
                                        <Badge
                                            {...subtleBadgeProps("secondary", "border")}
                                            pill
                                        >
                                            Origin:{" "}
                                            <span className="fw-semibold">{originLabel}</span>
                                        </Badge>
                                    ) : null}
                                </div>
                            </div>

                            {hasSubGroups ? (
                                <Tabs
                                    activeKey={activeTabKey}
                                    onSelect={(k) => setActiveSubGroupTab(k ?? "")}
                                    className="mb-3"
                                >
                                    {subGroups.map((sg) => {
                                        const key = String(sg.report_sub_group_id ?? "null");
                                        return (
                                            <Tab
                                                eventKey={key}
                                                title={sg.sub_group_name || "Sub-group"}
                                            >
                                                {key === activeTabKey ? renderAccordion() : null}
                                            </Tab>
                                        );
                                    })}
                                </Tabs>
                            ) : (
                                renderAccordion()
                            )}
                        </Card.Body>
                    </Card>
                )}
            </Modal.Body>

            <Modal.Footer className="border-0 pt-0">
                <div className="d-flex w-100 justify-content-between gap-2">
                    <Button variant="outline-secondary" onClick={onHide}>
                        Close
                    </Button>

                    <Button
                        variant="primary"
                        size="lg"
                        className="btn-action"
                        onClick={handleRefresh}
                        disabled={loading}
                        aria-busy={loading}
                    >
                        Refresh
                    </Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default PreviewReportTemplateModal;
